'use client';

import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import { BRANCH_ALL, BRANCH_OPTIONS } from '@constants/branch';

/**
 * Branches (cabang) in use by the caller's own PPJK. The API resolves the PPJK
 * from the session, so there is nothing to pass.
 *
 * Deliberately NOT a suspense query: this only decides whether a filter is
 * rendered, and blocking the whole document list on it would be a poor trade.
 * While it loads the list is empty, so the filter stays hidden until we know
 * there is something worth choosing between.
 */
async function fetchBranchNames(signal: AbortSignal): Promise<string[]> {
  const { data } = await axiosInstance.get<{ data: { branches: string[] | null } }>(apiUrl.docPabean.branchNames, {
    signal,
  });

  return data?.data?.branches ?? [];
}

export default function useGetBranchNames() {
  const { data, isError } = useQuery({
    queryKey: ['doc-pabean-branch-names'],
    queryFn: ({ signal }) => fetchBranchNames(signal),
    // Branches change when an office opens, not between page views.
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  // On failure fall back to the known pair rather than hiding the filter: a
  // tenant that genuinely has two offices would otherwise silently lose the
  // ability to switch between them.
  const rawBranches = isError ? BRANCH_OPTIONS.map((option) => option.value) : data ?? [];

  // The endpoint already returns distinct non-empty values, so this is a guard
  // rather than a correction. It matters for one case: `branch` is a free-text
  // column, so a tenant could name an office "ALL" — which is also the wire
  // value meaning "every branch". Offering it would render an option that reads
  // as that office but silently means the opposite, so it is dropped.
  const branches = [...new Set(rawBranches)].filter((branch) => branch && branch !== BRANCH_ALL);

  return {
    branches,
    // One branch (or none) means every option would return the same rows, so
    // the control is noise for every single-office tenant.
    hasMultipleBranches: branches.length > 1,
  };
}
